import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  HStack,
  Spacer,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import VaccineUsageSatisfactionChart from "./VaccineUsageSatisfactionChart";
import CustomDatePicker from "../../components/CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineUsageSatisfactionReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    StartDate: "",
    EndDate: "",
    VaccineNameTH: "",
    Gender: "",
    Occupation: "",
    Age: "",
    Criterion: "",
    Rating: "",
  });
  const today = new Date();
  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [criterionOptions, setCriterionOptions] = useState([
    "คุณภาพของวัคซีน (ความคุ้มโรค)",
    "ความปลอดภัยของวัคซีน (การแพ้วัคซีน, อาการข้างเคียงอื่นๆ)",
    "ราคาวัคซีน / โด๊ส",
    "ขนาดบรรจุ / ขวด",
    "ความเหมาะสมของบรรจุภัณฑ์",
    "ปริมาณวัคซีนเพียงพอต่อความต้องการ",
    "ความพึงพอใจเมื่อเทียบกับวัคซีนที่จำหน่ายโดยเอกชน",
    "กระบวนการ การให้บริการเพื่ออำนวยความสะดวก",
  ]);
  const [satisfactionData, setSatisfactionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ดึงข้อมูล Vaccine List
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // เรียก API getVaccineUsageSatisfactionSummary
  const fetchSatisfactionReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineUsageSatisfactionSummary",
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          VaccineNameTH: filterData.VaccineNameTH || "",
          Gender: filterData.Gender || "",
          Occupation: filterData.Occupation || "",
          Age: filterData.Age || "",
          Criterion: filterData.Criterion || "",
          Rating: filterData.Rating || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setSatisfactionData(response.summary || []);
      } else {
        setSatisfactionData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching satisfaction report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchVaccineOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>เพศ</span>
        </HStack>
      ),
      selector: (row) => row.Gender,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>อาชีพ</span>
        </HStack>
      ),
      selector: (row) => row.Occupation,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>อายุ</span>
        </HStack>
      ),
      selector: (row) => row.Age,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>เกณฑ์การประเมิน</span>
        </HStack>
      ),
      selector: (row) => row.Criterion,
      sortable: true,
      width: "300px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>คะแนนเฉลี่ย</span>
        </HStack>
      ),
      selector: (row) => row.AverageRating,
      sortable: true,
    },
  ];

  const exportToExcel = () => {
    const wsData = satisfactionData.map((item) => ({
      วัคซีน: item.VaccineNameTH,
      เพศ: item.Gender,
      อาชีพ: item.Occupation,
      อายุ: item.Age,
      เกณฑ์การประเมิน: item.Criterion,
      คะแนนเฉลี่ย: item.AverageRating,
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SatisfactionReport");
    XLSX.writeFile(wb, "VaccineUsageSatisfactionReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานสรุปผลจากแบบสอบถาม", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        ["วัคซีน", "เพศ", "อาชีพ", "อายุ", "เกณฑ์การประเมิน", "คะแนนเฉลี่ย"],
      ],
      body: satisfactionData.map((item) => [
        item.VaccineNameTH,
        item.Gender,
        item.Occupation,
        item.Age,
        item.Criterion,
        item.AverageRating,
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 12,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 20 },
        2: { cellWidth: 40 },
        3: { cellWidth: 30 },
        4: { cellWidth: 80 },
        5: { cellWidth: 30 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("VaccineUsageSatisfactionReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Vaccine Usage Satisfaction Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Vaccine Usage Satisfaction Report</h2>
          <table>
            <thead>
              <tr>
                <th>วัคซีน</th>
                <th>เพศ</th>
                <th>อาชีพ</th>
                <th>อายุ</th>
                <th>เกณฑ์การประเมิน</th>
                <th>คะแนนเฉลี่ย</th>
              </tr>
            </thead>
            <tbody>
              ${satisfactionData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.VaccineNameTH}</td>
                      <td>${item.Gender}</td>
                      <td>${item.Occupation}</td>
                      <td>${item.Age}</td>
                      <td>${item.Criterion}</td>
                      <td>${item.AverageRating}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานสรุปผลจากแบบสอบถาม" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลผลการใช้วัคซีน"
          bgColor="iBlue.600"
          icon={faSearch}
        />

        <VStack spacing={6} p={5}>
          <Grid templateColumns="repeat(4, 1fr)" gap={6} p={5}>
            <GridItem colSpan={1}>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  วันที่ทำแบบสอบถาม (เริ่มต้น)
                </FormLabel>
                <CustomDatePicker
                  name="StartDate"
                  value={filterData.StartDate}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  maxDate={today}
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  วันที่ทำแบบสอบถาม (สิ้นสุด)
                </FormLabel>
                <CustomDatePicker
                  name="EndDate"
                  value={filterData.EndDate}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  minDate={minDateForEndDate} // <--- ค่านี้จะเปลี่ยนไปตาม StartDate
                  maxDate={today}
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  เกณฑ์การประเมิน
                </FormLabel>
                <Select
                  name="Criterion"
                  value={filterData.Criterion}
                  onChange={handleInputChange}
                  placeholder="เลือกเกณฑ์ (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  {criterionOptions.map((criterion) => (
                    <option key={criterion} value={criterion}>
                      {criterion}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  ระดับความพึงพอใจ
                </FormLabel>
                <Select
                  name="Rating"
                  value={filterData.Rating}
                  onChange={handleInputChange}
                  placeholder="เลือกระดับ (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  วัคซีน
                </FormLabel>
                <Select
                  name="VaccineNameTH"
                  value={filterData.VaccineNameTH}
                  onChange={handleInputChange}
                  placeholder="เลือกวัคซีน (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  {vaccineOptions.map((vaccine) => (
                    <option
                      key={vaccine.VaccineID}
                      value={vaccine.VaccineNameTH}
                    >
                      {vaccine.VaccineNameTH}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  เพศ
                </FormLabel>
                <Select
                  name="Gender"
                  value={filterData.Gender}
                  onChange={handleInputChange}
                  placeholder="เลือกเพศ (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  <option value="M">ชาย</option>
                  <option value="F">หญิง</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  อาชีพ
                </FormLabel>
                <Select
                  name="Occupation"
                  value={filterData.Occupation}
                  onChange={handleInputChange}
                  placeholder="เลือกอาชีพ (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  <option value="เกษตรกร">เกษตรกร</option>
                  <option value="เจ้าหน้าที่กรมปศุสัตว์">
                    เจ้าหน้าที่กรมปศุสัตว์
                  </option>
                  <option value="สัตวแพทย์">สัตวแพทย์</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl minW="200px" flex="1">
                <FormLabel fontSize="xl" color="gray">
                  อายุ
                </FormLabel>
                <Select
                  name="Age"
                  value={filterData.Age}
                  onChange={handleInputChange}
                  placeholder="เลือกอายุ (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  <option value="Under20">ต่ำกว่า 20 ปี</option>
                  <option value="20-30">20-30 ปี</option>
                  <option value="31-40">31-40 ปี</option>
                  <option value="41Plus">41 ปีขึ้นไป</option>
                </Select>
              </FormControl>
            </GridItem>
          </Grid>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    StartDate: "",
                    EndDate: "",
                    VaccineNameTH: "",
                    Gender: "",
                    Occupation: "",
                    Age: "",
                    Criterion: "",
                    Rating: "",
                  })
                }
              />
              <Search onClick={fetchSatisfactionReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {satisfactionData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการสรุปผลจากแบบสอบถาม"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={satisfactionData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {satisfactionData.length > 0 && (
        <VaccineUsageSatisfactionChart
          data={satisfactionData}
          criterionOptions={criterionOptions}
        />
      )}
    </Box>
  );
};

export default VaccineUsageSatisfactionReport;
